import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, of } from 'rxjs';
import { map, exhaustMap, catchError, finalize } from 'rxjs/operators';

import { AuthActions } from '../_actions/auth.actions';
import { AuthService } from '../../_services/auth.service';
import { AppState } from '..';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import {
  LayoutUtilsServices,
  MessageType,
} from '../../_services/layout-utils.services';
import { LayoutActions } from '../_actions/layout.actions';
import { isPlatformBrowser } from '@angular/common';

@Injectable()
export class AuthEffects {
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginAuth),
      exhaustMap((action) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._authService.login(action.username, action.password).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((res) => {
            this._layoutUtilsServices.showNotification({
              message: 'Bienvenido',
              type: MessageType.Susses,
            });
            this.router.navigate(['/sales/dasboard']);
            return AuthActions.loginAuthSuccess({
              token: res.access_token,
              status: true,
            });
          }),
          catchError(({ error }) => {
            this._layoutUtilsServices.showNotification({
              message: 'Usuario o contraseña incorrectos',
              type: MessageType.Danger,
            });
            return EMPTY;
          })
        );
      })
    )
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginAuthSuccess),
        map(({ token }) => {
          if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem('token', token);
            this.store.dispatch(AuthActions.loadUserAuth());
          }
        })
      ),
    { dispatch: false }
  );

  loadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loadUserAuth),
      exhaustMap((action) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._authService.me().pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((user) => AuthActions.loadUserAuthSuccess({ user })),
          catchError(() => EMPTY)
        );
      })
    )
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutAuth),
        map(() => {
          if (isPlatformBrowser(this.platformId)) {
            localStorage.removeItem('token');
          }
          this.router.navigate(['/auth/login']);
        })
      ),
    { dispatch: false }
  );

  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.registerAuth),
      exhaustMap((action) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._authService.register(action.data).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((res) => {
            this._layoutUtilsServices.showNotification({
              message: 'Se ha registrado exitosamente',
              type: MessageType.Susses,
            });
            setTimeout(
              () => this.router.navigate(['/auth/account-activation']),
              1000
            );
            return AuthActions.loginAuthSuccess({
              token: res.access_token,
              status: true,
            });
          }),
          catchError(({ error }) => {
            this._layoutUtilsServices.showNotification({
              message:
                error?.message ||
                'Error al registro, por favor intente de nuevo',
              type: MessageType.Danger,
            });
            return EMPTY;
          })
        );
      })
    )
  );
  editUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.editUserAuth),
      exhaustMap((action) => {
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._authService.update(action.data).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((res) => {
            this._layoutUtilsServices.showNotification({
              message: 'Se ha actualizado el registro exitosamente',
              type: MessageType.Susses,
            });
            // this.router.navigate(['/users']);
            this.store.dispatch(AuthActions.loadUserAuth());
            return AuthActions.editUserAuthSuccess({ data: res });
          }),
          catchError(({ error }) => {
            this._layoutUtilsServices.showNotification({
              message:
                error?.message ||
                'Error al actualizar el registro, por favor intente de nuevo',
              type: MessageType.Danger,
            });
            return EMPTY;
          })
        );
      })
    )
  );

  forgot$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.forgotAuth),
      exhaustMap((action) => {
        console.log(action);
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._authService.forgot(action.email).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((user) => {
            return AuthActions.forgotAuthSuccess();
          }),
          catchError(({ error }) => {
            this._layoutUtilsServices.showNotification({
              message: 'Usuario incorrectos',
              type: MessageType.Danger,
            });
            return EMPTY;
          })
        );
      })
    )
  );

  reset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resetPasswordAuth),
      exhaustMap((action) => {
        console.log(action);
        this.store.dispatch(LayoutActions.spinnerTrigger({ show: true }));
        return this._authService.reset(action.hash, action.password).pipe(
          finalize(() =>
            this.store.dispatch(LayoutActions.spinnerTrigger({ show: false }))
          ),
          map((res) => {
            this._layoutUtilsServices.showNotification({
              message: 'Bienvenido',
              type: MessageType.Susses,
            });
            this.router.navigate(['/sales/dasboard']);
            return AuthActions.loginAuthSuccess({
              token: res.access_token,
              status: true,
            });
          }),
          catchError(({ error }) => {
            this._layoutUtilsServices.showNotification({
              message: 'Usuario o clave incorrectos',
              type: MessageType.Danger,
            });
            return EMPTY;
          })
        );
      })
    )
  );

  init$ = createEffect(() => {
    let observableResult = of({ type: 'INI_ACTION' });
    if (isPlatformBrowser(this.platformId)) {
      const userToken = localStorage.getItem('token');
      if (userToken) {
        observableResult = of(
          AuthActions.loginAuthSuccess({
            token: userToken,
            status: true,
          })
        );
      }
    }
    return observableResult;
  });

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private router: Router,
    private _authService: AuthService,
    private _layoutUtilsServices: LayoutUtilsServices,
    @Inject(PLATFORM_ID) private platformId: object
  ) {}
}
